import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  verifyPayment,
  selectPaymentVerifying,
  selectPaymentVerified,
  selectPaymentError,
  fetchOrderDetails,
} from "../features/payment/paymentSlice";
import { selectToken } from "../features/auth/authSlice";
import { selectLanguage } from "../features/language/languageSlice";
import type { AppDispatch } from "../store";
import brandManager from "../services/brandManager";

export default function PaymentReturnHandler() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const token = useSelector(selectToken);
  const language = useSelector(selectLanguage);
  const verifying = useSelector(selectPaymentVerifying);
  const verified = useSelector(selectPaymentVerified); // 'paid' | 'failed' | 'canceled' | null
  const verificationError = useSelector(selectPaymentError);

  const [verificationAttempted, setVerificationAttempted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // يمنع التنقل المكرر لأي صفحة
  const handledRef = useRef(false);

  // دالة مساعدة للحصول على token بديل
  const getFallbackToken = (): string | null => {
    const fallbackToken = 
      sessionStorage.getItem('authToken') || 
      sessionStorage.getItem('orderToken') || 
      sessionStorage.getItem('currentOrderToken') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('orderToken');
    
    return fallbackToken;
  };

  // دالة مساعدة للحصول على orderId من sessionStorage
  const getOrderIdFromStorage = (): string | null => {
    const orderDataStr = sessionStorage.getItem("orderData");
    if (orderDataStr) {
      try {
        const orderData = JSON.parse(orderDataStr);
        return (
          orderData.orderId?.toString?.() ||
          orderData.id?.toString?.() ||
          null
        );
      } catch {}
    }
    
    const paymobOrderId = sessionStorage.getItem("paymob_order_id");
    if (paymobOrderId) {
      return paymobOrderId;
    }
    
    return null;
  };

  // دالة لضمان تحميل الموارد الحرجة
  const ensureCriticalResourcesLoaded = async (): Promise<void> => {
    return new Promise((resolve) => {
      // تحقق من تحميل CSS
      const checkCSSLoaded = () => {
        const stylesheets = document.styleSheets;
        let cssLoaded = false;
        
        for (let i = 0; i < stylesheets.length; i++) {
          try {
            const sheet = stylesheets[i];
            if (sheet.href && sheet.href.includes('main.') && sheet.href.includes('.css')) {
              cssLoaded = true;
              break;
            }
          } catch (e) {
            // تجاهل الأخطاء في الوصول للـ stylesheets
          }
        }
        
        return cssLoaded;
      };
      
      // تحقق من تحميل JavaScript
      const checkJSLoaded = () => {
        return window.React !== undefined || document.querySelector('script[src*="main."]') !== null;
      };
      
      // دالة لإعادة تحميل الموارد المفقودة
      const reloadMissingResources = () => {
        
        // إعادة تحميل CSS إذا لم يكن محمل
        if (!checkCSSLoaded()) {
          const cssLink = document.createElement('link');
          cssLink.rel = 'stylesheet';
          cssLink.href = '/static/css/main.532b4541.css';
          cssLink.onload = () => {};
          cssLink.onerror = () => {};
          document.head.appendChild(cssLink);
        }
        
        // إعادة تحميل JavaScript إذا لم يكن محمل
        if (!checkJSLoaded()) {
          const jsScript = document.createElement('script');
          jsScript.src = '/static/js/main.24c46377.js';
          jsScript.onload = () => {};
          jsScript.onerror = () => {};
          document.head.appendChild(jsScript);
        }
      };
      
      // إذا كانت الموارد محملة بالفعل، انتقل فوراً
      if (checkCSSLoaded() && checkJSLoaded()) {
        resolve();
        return;
      }
      
      // محاولة إعادة تحميل الموارد المفقودة
      reloadMissingResources();
      
      // انتظر تحميل الموارد
      let attempts = 0;
      const maxAttempts = 50; // 5 ثواني كحد أقصى
      
      const checkResources = () => {
        attempts++;
        
        if (checkCSSLoaded() && checkJSLoaded()) {
          resolve();
        } else if (attempts >= maxAttempts) {
          resolve();
        } else {
          setTimeout(checkResources, 100);
        }
      };
      
      checkResources();
    });
  };

  // دالة معالجة الدفع الناجح
  const handleSuccessfulPayment = async (orderId: string) => {
    
    // نظف أثر الدفع واحتفظ فقط بما تحتاجه صفحة الطلبات
    sessionStorage.removeItem("pay_intent");
    sessionStorage.removeItem("paymentData");
    sessionStorage.removeItem("paymobUrl");
    sessionStorage.removeItem("user_leaving_payment");

    // لو عندك token خاص بالطلب كنت خزنته قبل التحويل
    const orderToken = sessionStorage.getItem("orderToken");
    const orderDataStr = sessionStorage.getItem("orderData");
    const orderCartDataStr = sessionStorage.getItem("orderCartData");
    
    
    if (orderId && orderToken && orderDataStr && orderCartDataStr) {
      // تأكد من أن جميع البيانات متوفرة
      sessionStorage.setItem("currentOrderToken", orderToken);
      sessionStorage.setItem("currentOrderId", orderId);
      
      // تحسين حفظ البيانات لضمان توفرها فوراً
      try {
        const parsedOrderData = JSON.parse(orderDataStr);
        const parsedCartData = JSON.parse(orderCartDataStr);
        
        // إنشاء بيانات طلب محسنة للعرض الفوري
        const enhancedOrderData = {
          ...parsedOrderData,
          id: orderId,
          order_number: orderId,
          payment_status: 'paid',
          status: 'confirmed',
          created_at: new Date().toISOString(),
          // إضافة بيانات العميل من URL parameters
          customer_phone: parsedOrderData.customerInfo?.phone || "غير متوفر",
          user: {
            name: parsedOrderData.customerInfo?.name || "غير متوفر",
            phone: parsedOrderData.customerInfo?.phone || "غير متوفر"
          },
          // إضافة بيانات الفرع
          branch: parsedOrderData.urlParams?.branchName || "غير متوفر",
          // إضافة طريقة الدفع
          payment_method: "online",
          paymentMethod: "online",
          // إضافة بيانات الطاولة إذا كانت متوفرة
          ...(parsedOrderData.urlParams?.tableId && {
            table: {
              id: parsedOrderData.urlParams.tableId,
              name: parsedOrderData.urlParams.tableName || `طاولة ${parsedOrderData.urlParams.tableId}`,
              table_price: parsedOrderData.urlParams.tablePrice || "0"
            },
            table_price: parsedOrderData.urlParams.tablePrice || "0"
          }),
          // إضافة بيانات المنتجات من السلة
          items: parsedCartData.items || [],
          total_price: parsedCartData.total_price || 0,
          subtotal: parsedCartData.price || parsedCartData.total_price || 0,
          tax_value: 0, // سيتم تحديثها من API لاحقاً
          discount: 0
        };
        
        sessionStorage.setItem("currentOrderData", JSON.stringify(enhancedOrderData));
        sessionStorage.setItem("currentOrderCartData", orderCartDataStr);
        
      } catch (error) {
        sessionStorage.setItem("currentOrderData", orderDataStr);
        sessionStorage.setItem("currentOrderCartData", orderCartDataStr);
      }

      // Store additional data for easy access
      sessionStorage.setItem('orderDetailsUrl', `/order-success?orderId=${orderId}`)
      sessionStorage.setItem('orderApiUrl', `${brandManager.apiUrl}/qr/orders/${orderId}`)
      
      // تأكد من أن token المصادقة لا يزال صالحاً
      const authToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      if (authToken) {
        sessionStorage.setItem("authToken", authToken);
        
        // 🚀 استدعاء API فوري لجلب بيانات الطلب قبل التوجيه
        console.log('🚀 PaymentReturnHandler: Making immediate API call for order:', orderId)
        dispatch(
          fetchOrderDetails({
            orderId: orderId,
            token: authToken,
            lang: language,
          })
        ).then((result) => {
          console.log('✅ PaymentReturnHandler: API call successful:', result)
          if (result.type === 'payment/fetchOrderDetails/fulfilled') {
            // حفظ البيانات المحملة من API
            sessionStorage.setItem("currentOrderData", JSON.stringify(result.payload))
            console.log('💾 PaymentReturnHandler: Order data saved to sessionStorage')
          }
        }).catch((error) => {
          console.error('❌ PaymentReturnHandler: API call failed:', error)
        })
      }
      
      // تقليل التأخير لتحسين تجربة المستخدم
      await new Promise(resolve => setTimeout(resolve, 200)); // تقليل التأخير أكثر
      
      // 🔧 الحل الجديد: تأكد من تحميل الموارد الحرجة قبل التنقل
      await ensureCriticalResourcesLoaded();
      
      // التوجيه إلى صفحة النجاح مع رقم الطلب
      navigate(`/order-success?orderId=${orderId}`, { replace: true });
    } else {
      // fallback للنجاح العام
      
      // تأكد من تحميل الموارد الحرجة حتى في حالة fallback
      await ensureCriticalResourcesLoaded();
      navigate("/order-success", { replace: true });
    }
  };

  // 1) بدء التحقق بأمان
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hasPaymentParams =
      urlParams.has("hmac") ||
      urlParams.has("paymob_order_id") ||
      urlParams.has("merchant_order_id") ||
      urlParams.has("amount_cents") ||
      urlParams.has("success");

    if (!hasPaymentParams) return; // مش رجوع دفع

    // علّم إننا بقينا في مرحلة التحقق لتفادي اعتبارها "abandoned"
    sessionStorage.setItem("pay_state", "verifying");
    sessionStorage.removeItem("user_leaving_payment");

    const params = {
      ...Object.fromEntries(urlParams.entries()),
      // ننضف القيم الفارغة
      success: urlParams.get("success") ?? undefined,
      paymob_order_id: urlParams.get("paymob_order_id") ?? undefined,
      merchant_order_id: urlParams.get("merchant_order_id") ?? undefined,
      amount_cents: urlParams.get("amount_cents") ?? undefined,
      hmac: urlParams.get("hmac") ?? undefined,
    };

    setIsProcessing(true);

    // 🔧 الحل الجديد: التحقق من معاملات URL أولاً
    const successParam = urlParams.get("success");
    const amountCents = urlParams.get("amount_cents");
    
    
    // إذا كان success=true و amount_cents موجود، فهذا يعني نجاح الدفع
    if (successParam === "true" && amountCents) {
      setVerificationAttempted(true);
      setIsProcessing(false);
      
      // احصل على orderId من sessionStorage أو URL
      const orderId = getOrderIdFromStorage() || params.merchant_order_id;
      
      if (orderId) {
        // نجح الدفع - انتقل إلى صفحة النجاح
        handleSuccessfulPayment(orderId);
      } else {
        // لا يوجد orderId - انتقل إلى صفحة النجاح العامة
        navigate("/order-success", { replace: true });
      }
      return;
    }

    // إذا كان success=false، فهذا يعني فشل الدفع
    if (successParam === "false") {
      setVerificationAttempted(true);
      setIsProcessing(false);
      navigate("/error?reason=payment_failed", { replace: true });
      return;
    }

    // 🔧 الحل الجديد: محاولة التحقق مع token أو بدونه
    const tokenToUse = token || getFallbackToken();
    
    if (tokenToUse) {
      dispatch(
        verifyPayment({
          params,
          token: tokenToUse,
          lang: language,
        })
      ).finally(() => {
        setVerificationAttempted(true);
        setIsProcessing(false);
      });
    } else {
      // لا يوجد token - نحاول التحقق من البيانات المتوفرة
      
      // تحقق من البيانات المخزنة في sessionStorage
      const payIntent = sessionStorage.getItem('pay_intent');
      const payState = sessionStorage.getItem('pay_state');
      
      if (payIntent && payState === 'pending') {
        // يوجد intent معلق - انتقل إلى صفحة الخطأ مع إمكانية المحاولة مرة أخرى
        setVerificationAttempted(true);
        setIsProcessing(false);
        navigate("/error?reason=payment_pending", { replace: true });
      } else {
        // لا توجد بيانات كافية - انتقل إلى صفحة الخطأ
        setVerificationAttempted(true);
        setIsProcessing(false);
        navigate("/error?reason=verification_error", { replace: true });
      }
    }
  }, [dispatch, token, language, navigate]);

  // 2) التصرف بعد نتيجة التحقق فقط
  useEffect(() => {
    if (handledRef.current) return; // منع تكرار التوجيه
    if (!verificationAttempted) return;
    if (verifying) return; // لسه بيتحقق

    // دالة مساعدة للحصول على orderId بشكل موثوق
    const getOrderId = (): string | null => {
      // أولوية: بيانات الجلسة التي خزّنتها قبل التحويل لبوابة الدفع
      const orderDataStr = sessionStorage.getItem("orderData");
      if (orderDataStr) {
        try {
          const orderData = JSON.parse(orderDataStr);
          return (
            orderData.orderId?.toString?.() ||
            orderData.id?.toString?.() ||
            null
          );
        } catch {}
      }
      
      // بديل: من paymob_order_id المخزن في sessionStorage
      const paymobOrderId = sessionStorage.getItem("paymob_order_id");
      if (paymobOrderId) {
        return paymobOrderId;
      }
      
      // بديل: من URL
      const urlParams = new URLSearchParams(window.location.search);
      return (
        urlParams.get("merchant_order_id") ||
        urlParams.get("order_id") ||
        null
      );
    };

    if (verified === "paid") {
      handledRef.current = true;
      sessionStorage.setItem("pay_state", "success");

      const orderId = getOrderId();
      if (orderId) {
        handleSuccessfulPayment(orderId);
      } else {
        navigate("/order-success", { replace: true });
      }
      return;
    }

    // في حالة فشل أو إلغاء فقط
    if (verified === "failed" || verified === "canceled") {
      handledRef.current = true;
      sessionStorage.setItem("pay_state", "failed");
      const reason = verified === "canceled" ? "payment_canceled" : "payment_failed";
      navigate(`/error?reason=${reason}`, { replace: true });
      return;
    }

    // خطأ في التحقق من السيرفر (ولكن مش "مدفوع")
    if (verificationError) {
      handledRef.current = true;
      sessionStorage.setItem("pay_state", "failed");
      navigate("/error?reason=verification_error", { replace: true });
    }
  }, [verificationAttempted, verifying, verified, verificationError, navigate]);

  // 3) منع اعتبار الرجوع Abandoned أثناء التحقق أو بعد نجاحه
  useEffect(() => {
    const handleBeforeUnload = () =>
      sessionStorage.setItem("user_leaving_payment", "true");

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      const payState = sessionStorage.getItem("pay_state");
      // طالما في verifying أو success، ما نعتبرهاش مهجورة
      if (payState === "pending") {
        sessionStorage.setItem("pay_state", "failed");
        // بإمكانك إعادة التوجيه لصفحة retry هنا لو حابب
        // navigate('/error?reason=payment_abandoned', { replace: true });
      }
      sessionStorage.removeItem("user_leaving_payment");
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  if (isProcessing || verifying) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">جاري معالجة عملية الدفع...</p>
        </div>
      </div>
    );
  }

  return null;
}
